import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  rem,
} from "@mantine/core";
import { IconCoins, IconSettings } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  getGenerationOptions,
  getJobResults,
  getPlugins,
  pollJob,
  runPlugin,
} from "../api";
import { useStore } from "../store";
import V2PluginRunForm from "../SuperGallery/V2PluginRunForm";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Renders the plugin edit-features for one image on the /editor details page.
 *
 * For every installed v2 plugin that can consume this image (a "source" mode),
 * it derives two tiers from the same options payload the generator rail uses:
 *
 *  - Instant actions: single-output source modes whose gated prompt is fully
 *    authored (no text slot to fill). Each preset ("Strength") becomes its own
 *    one-click chip — running a filter preset on this image is a single tap.
 *  - A "more options" link opens the standard plugin run form in a modal,
 *    prefilled with this image as the source, for interactive modes (e.g.
 *    prompt-driven edits) that still need typed input.
 *
 * Fixed-filter runs poll to completion and swap the details view to the result,
 * mirroring the in-place behaviour of the legacy operation buttons. Modal runs
 * keep their rail behaviour (outputs land in the gallery) because they may fan
 * out several images.
 */
export default function EditorQuickActions({ imageId, disabled }) {
  const plugins = useStore((state) => state.pluginData);
  const setPluginData = useStore((state) => state.setPluginData);
  const setImageData = useStore((state) => state.setImageData);

  // Keep pluginData in sync with the manifests actually installed server-side
  // (pluginData persists in localStorage and can go stale).
  useEffect(() => {
    getPlugins().then(setPluginData).catch(() => {});
  }, [setPluginData]);

  const candidates = useMemo(
    () =>
      (plugins ?? []).filter((p) => {
        if (!p.isManifestPlugin || !p.id) return false;
        const modes = p.v2Modes ?? [];
        if (modes.length > 0)
          return modes.some(
            (m) => (!m.media || m.media === "image") && m.requiresSourceImage
          );
        return !!p.requiresSourceImage && !p.isVideo;
      }),
    [plugins]
  );

  const [loaded, setLoaded] = useState([]); // { pluginId, pluginTitle, options }
  const [loading, setLoading] = useState(false);
  const candidatesKey = candidates.map((p) => p.id).join(",");

  useEffect(() => {
    if (candidates.length === 0) {
      setLoaded([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      candidates.map(async (p) => {
        try {
          const options = await getGenerationOptions(p.id, "image");
          return { pluginId: p.id, pluginTitle: p.pluginTitle, options };
        } catch (err) {
          console.warn(`Failed to load generation options for ${p.pluginTitle}`, err);
          return null;
        }
      })
    )
      .then((results) => {
        if (cancelled) return;
        setLoaded(results.filter(Boolean));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidatesKey]);

  // Every plugin with at least one usable source mode on this surface becomes a
  // section; instant chips are the fixed modes, everything else routes to the
  // full run form in the modal.
  const sections = useMemo(
    () =>
      loaded.map(({ pluginId, pluginTitle, options }) => {
        const { instant, interactive, gateName } = deriveEditorActions(options);
        return {
          pluginId,
          pluginTitle,
          gateName,
          interactive,
          instant: instant.map((a) => ({ ...a, pluginId, gateName })),
        };
      }),
    [loaded]
  );

  const visibleSections = sections.filter(
    (s) => s.instant.length > 0 || s.interactive
  );

  const [busyKey, setBusyKey] = useState(null);
  const abortRef = useRef(null);
  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const [modalPluginId, setModalPluginId] = useState(null);
  const modalPlugin = loaded.find((p) => p.pluginId === modalPluginId);

  const openFullForm = (pluginId) => setModalPluginId(pluginId);
  const closeFullForm = () => setModalPluginId(null);

  const runInstant = async (action) => {
    setBusyKey(action.key);
    const abort = new AbortController();
    abortRef.current = abort;
    try {
      const base = { [action.imageInputName]: imageId };
      if (action.gateName && action.gateName !== action.imageInputName)
        base[action.gateName] = action.mode.value;
      const presets =
        action.gateName && action.preset
          ? { [action.gateName]: action.preset.id }
          : {};
      const res = await runPlugin(action.pluginId, base, {}, presets, undefined);

      let next = null;
      for (const jobId of res?.jobIds ?? []) {
        await waitForJobResult(jobId, abort.signal);
        const data = await getJobResults(jobId, { signal: abort.signal });
        const img = data?.results?.[0];
        if (img) next = img;
      }
      if (next) {
        setImageData(next);
        notifications.show({
          title: "Edit complete",
          message: `${action.label} finished — showing the result.`,
          color: "green",
        });
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      notifications.show({
        title: "Edit failed",
        message:
          err?.message ||
          "The plugin run failed. If the model refused the image, credits were not charged.",
        color: "red",
      });
    } finally {
      setBusyKey(null);
      abortRef.current = null;
    }
  };

  if (loading && visibleSections.length === 0) {
    return (
      <Group gap="xs" align="center" p="xs">
        <Loader size="xs" />
        <Text fz="xs" c="dimmed">
          Loading quick edits…
        </Text>
      </Group>
    );
  }

  if (visibleSections.length === 0) return null;

  return (
    <Stack gap="sm" mt="xl">
      <Text fz="sm" c="dimmed" fw={600}>
        Edit with plugins
      </Text>
      {visibleSections.map((section) => (
        <Box key={section.pluginId}>
          <Group justify="space-between" wrap="nowrap" gap="xs">
            <Text fz="xs" fw={600}>
              {section.pluginTitle}
            </Text>
            {section.interactive && (
              <Button
                size="compact-xs"
                variant="subtle"
                disabled={!!busyKey || disabled}
                leftSection={
                  <IconSettings
                    style={{ width: rem(13), height: rem(13) }}
                    stroke={1.5}
                  />
                }
                onClick={() => openFullForm(section.pluginId)}
              >
                More options
              </Button>
            )}
          </Group>
          {section.instant.length > 0 && (
            <Group gap={4} wrap="wrap">
              {section.instant.map((action) => (
                <Button
                  key={action.key}
                  variant="light"
                  size="compact-xs"
                  disabled={!!busyKey || disabled}
                  loading={busyKey === action.key}
                  loaderProps={{ type: "dots" }}
                  onClick={() => runInstant(action)}
                  leftSection={
                    action.credits != null ? (
                      <Group wrap="nowrap" gap={2}>
                        <IconCoins
                          stroke={1.5}
                          style={{ width: rem(12), height: rem(12) }}
                        />
                        {action.credits}
                      </Group>
                    ) : undefined
                  }
                >
                  {action.label}
                </Button>
              ))}
            </Group>
          )}
        </Box>
      ))}

      <Modal
        opened={!!modalPlugin}
        onClose={closeFullForm}
        title={modalPlugin ? `Edit image with ${modalPlugin.pluginTitle}` : ""}
        size="md"
      >
        {modalPlugin && (
          <ScrollArea.Autosize mah="70vh" mx={-16} px={16}>
            <V2PluginRunForm
              pluginId={modalPlugin.pluginId}
              options={modalPlugin.options}
              sourceImageId={imageId}
              onSubmitted={() => {
                // Outputs queue into the shared jobs state; the gallery poll loop
                // picks them up when the user returns. Nothing more to do here.
              }}
            />
          </ScrollArea.Autosize>
        )}
      </Modal>
    </Stack>
  );
}

/**
 * Polls a job until it reaches a terminal state. Resolves on success; rejects
 * with a user-facing message on failure.
 */
async function waitForJobResult(jobId, signal) {
  for (;;) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const dto = await pollJob(jobId, { signal });
    const status = dto?.status;
    if (status === "Success") return;
    if (status === "Failed")
      throw new Error(
        "The edit job failed — the model may have refused this image."
      );
    await sleep(2500);
  }
}

/**
 * Derives the quick-edit tiers for a plugin from its /generation/options payload.
 *
 * - `instant`: gate options that consume one source image, output a single image
 *   and whose active gated prompt has no Text slot to fill (fixed-prompt filters).
 *   Each declared preset becomes its own chip; a preset-less mode is one chip.
 * - `interactive`: true when a source-consuming mode still needs typed input
 *   (e.g. a prompt-driven edit) and should open the full run form instead.
 */
function deriveEditorActions(options) {
  const inputs = options?.v2Inputs ?? [];
  const imageInputs = inputs.filter((i) => i.kind === "Image");
  const imageInputName = imageInputs.length === 1 ? imageInputs[0].name : null;
  const gate = inputs.find((i) =>
    (i.options ?? []).some((o) => o.estimatedCredits != null)
  );

  const sourceModes = [];
  if (gate) {
    for (const opt of gate.options ?? []) {
      if (!opt.requiresSourceImage) continue;
      if (opt.videoModel) continue; // image edits only
      if ((opt.outputCount ?? 1) !== 1) continue; // keep it to a single derivative
      const activeInputs = inputs.filter(
        (i) =>
          i.name !== gate.name &&
          (!i.modes?.length || i.modes.includes(opt.value))
      );
      const activeImages = activeInputs.filter((i) => i.kind === "Image");
      // A single-source edit: exactly one Image slot to fill (this one). A mode
      // that wants a reference + source pair can't run from the details page.
      if (activeImages.length !== 1) continue;
      const activeNonImage = activeInputs.filter((i) => i.kind !== "Image");
      sourceModes.push({
        mode: opt,
        textInputs: activeNonImage.filter((i) => i.kind === "Text"),
        requiredNonImage: activeNonImage.filter((i) => i.required),
        imageInputName: activeImages[0].name,
      });
    }
  } else if (imageInputName && (options?.v2OutputCount ?? 1) === 1) {
    // No exclusive gate: a single-purpose image plugin. It edits this image when
    // its recipe is fully authored (pure filter) or opens the form when it needs
    // typed input.
    const textInputs = inputs.filter((i) => i.kind === "Text");
    const requiredNonImage = inputs.filter(
      (i) => i.kind !== "Image" && i.required
    );
    sourceModes.push({
      mode: { label: options?.v2RunButtonLabel || options?.title || "Edit" },
      textInputs,
      requiredNonImage,
      imageInputName,
    });
  }

  const instant = [];
  let interactive = false;
  for (const entry of sourceModes) {
    const isInstant =
      entry.textInputs.length === 0 && entry.requiredNonImage.length === 0;
    if (!isInstant) {
      interactive = true;
      continue;
    }
    const presets = entry.mode.presets ?? [];
    if (presets.length > 0) {
      for (const preset of presets) {
        instant.push({
          key: `m=${entry.mode.value}:p=${preset.id}`,
          label: `${entry.mode.label} · ${preset.label}`,
          mode: entry.mode,
          preset,
          imageInputName: entry.imageInputName,
          credits: entry.mode.estimatedCredits ?? options?.v2EstimatedCredits,
        });
      }
    } else {
      instant.push({
        key: `m=${entry.mode.value ?? "default"}:p=default`,
        label: entry.mode.label,
        mode: entry.mode,
        preset: null,
        imageInputName: entry.imageInputName,
        credits: entry.mode.estimatedCredits ?? options?.v2EstimatedCredits,
      });
    }
  }

  if (instant.length === 0 && !interactive)
    return { instant: [], interactive: false, gateName: null };

  return {
    instant,
    interactive,
    gateName: gate?.name ?? null,
  };
}
